/**
 * Dense, compositional table for operator views (call logs, numbers, agents).
 *
 *   <Table>
 *     <THead><TR><TH>Agent</TH><TH align="right">Calls</TH></TR></THead>
 *     <TBody>
 *       <TR interactive onClick={...}><TD>…</TD><TD align="right" mono>42</TD></TR>
 *     </TBody>
 *   </Table>
 */

import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";

import { cx } from "../lib/format";

export function Table({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableElement> & { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded border border-line">
      <table className={cx("w-full border-collapse text-sm", className)} {...rest}>
        {children}
      </table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return <thead className="border-b border-line bg-surface">{children}</thead>;
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-line">{children}</tbody>;
}

export function TR({
  interactive = false,
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean; children: ReactNode }) {
  return (
    <tr
      className={cx(
        interactive && "cursor-pointer transition-colors hover:bg-surface-raised",
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

type Align = "left" | "right" | "center";

const ALIGN: Record<Align, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

export function TH({
  align = "left",
  className,
  children,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { align?: Align; children?: ReactNode }) {
  return (
    <th
      scope="col"
      className={cx(
        "whitespace-nowrap px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted",
        ALIGN[align],
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export function TD({
  align = "left",
  mono = false,
  className,
  children,
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & {
  align?: Align;
  /** Mono face for numbers, IDs, phone numbers. */
  mono?: boolean;
  children?: ReactNode;
}) {
  return (
    <td
      className={cx(
        "whitespace-nowrap px-3 py-2 text-body",
        ALIGN[align],
        mono && "font-mono text-xs",
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}
